/**
 * ImagePreviewModal Component
 *
 * Fullscreen modal for viewing AI Studio generated images with action buttons.
 * Based on the ai-avatar ImageViewModal pattern.
 */

import { X, Download, Trash2 } from 'lucide-react';
import { Dialog, DialogOverlay, DialogPortal } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { IImagePreviewModalProps } from '@/@types/ai-studio';

export function ImagePreviewModal({
  session,
  isOpen,
  onClose,
  onDownload,
  onDelete,
}: IImagePreviewModalProps) {
  if (!session || !session.imageUrl) return null;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay className="bg-black/90" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-50 flex items-center justify-center p-4 outline-none"
          onEscapeKeyDown={onClose}
          onClick={e => {
            // Close when clicking on the backdrop (the content div itself), not on children
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-10 flex size-10 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70"
          >
            <X className="size-5" />
          </button>

          {/* Image Container - stop propagation to prevent closing when clicking on image */}
          <div
            role="presentation"
            className="relative max-h-[85vh] max-w-[90vw]"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
          >
            <img
              src={session.imageUrl}
              alt={session.params.sceneName}
              className="max-h-[85vh] max-w-full rounded-lg object-contain"
            />

            {/* Scene Info Overlay (top) */}
            <div className="absolute left-4 top-4 rounded-lg bg-black/70 px-3 py-2 backdrop-blur-sm">
              <p className="text-sm font-medium text-white">{session.params.sceneName}</p>
              <p className="text-xs text-white/70">{session.params.sceneCategory}</p>
            </div>

            {/* Status Badge */}
            <div className="absolute right-16 top-4 z-10">
              <span className="rounded-md bg-primary/90 px-2 py-1 text-xs font-medium text-primary-foreground">
                Generated
              </span>
            </div>

            {/* Action Buttons (bottom) */}
            <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/70 p-2 backdrop-blur-sm">
              <Button
                variant="ghost"
                size="sm"
                onClick={onDownload}
                className="text-white hover:bg-white/20 hover:text-white"
              >
                <Download className="size-4" />
                <span>Download</span>
              </Button>

              <div className="h-6 w-px bg-white/30" />

              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onDelete();
                  onClose();
                }}
                className="text-destructive hover:bg-destructive/20 hover:text-destructive"
              >
                <Trash2 className="size-4" />
                <span>Delete</span>
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
